import React, { useContext } from 'react'
import { BodyText, Card, Divider } from '@paypalcorp/pp-react'
import { CleverContext } from './cleverContext'
import {
  isApplicationAutomationEligible,
  roundOffNumberValue,
  getNumberFromString,
  convertTermToWeeks,
} from '../../utils/clever-utils'
import { AUTO_QUALIFICATION_FAILURE_REASONS_MAP } from '../../utils/constants'
import {
  getLocalizedText,
  getCurrencyFormat,
} from 'ppbl/utils/locale-helper.js'

type Props = {
  contentCardType: string
}

/**
 * This is a functional card component that displays information
 * @returns {JSX.Element} - A JSX element
 */
const InformationCard = ({ contentCardType }: Props) => {
  const { application, loanAmount } = useContext(CleverContext)

  const getCardHeader = (cardType) => {
    switch (cardType) {
      case 'Opportunity Information':
        return getLocalizedText(
          'cleverCalculator-page.opportunityInformation.opportunityInformationText',
        )

      case 'Renewal Information':
        return 'Renewal Information:'

      case 'Prior Loan Information':
        return 'Prior Loan Details:'

      default:
        return ''
    }
  }

  const getCardContent = (cardType) => {
    switch (cardType) {
      case 'Opportunity Information': {
        const grade = application?.evaluation?.underwriting?.grade || '-'
        const knockoutReason =
          AUTO_QUALIFICATION_FAILURE_REASONS_MAP[
            application?.evaluation?.autoQualificationFailureReason
          ] || '-'
        const isAutomationEligible = isApplicationAutomationEligible(
          application?.tags,
        )
        return [
          {
            value: grade,
            label: getLocalizedText(
              'cleverCalculator-page.opportunityInformation.gradeText',
            ),
          },
          {
            value: isAutomationEligible,
            label: getLocalizedText(
              'cleverCalculator-page.opportunityInformation.automationEligibilityText',
            ),
          },
          {
            value: knockoutReason,
            label: getLocalizedText(
              'cleverCalculator-page.opportunityInformation.automationKnockoutsText',
            ),
          },
        ]
      }

      case 'Renewal Information': {
        const loanHistory = application?.business?.loanHistory
        const formattedTotalOutstandingInterestBalance = getNumberFromString(
          loanHistory?.formattedTotalOutstandingInterestBalance.amount,
        )
        const formattedTotalOutstandingPrincipalBalance = getNumberFromString(
          loanHistory?.formattedTotalOutstandingPrincipalBalance.amount,
        )
        const netCashRecieved = roundOffNumberValue(
          Number(loanAmount) -
            Number(formattedTotalOutstandingPrincipalBalance),
        )
        return [
          {
            value: getCurrencyFormat(
              Number(formattedTotalOutstandingInterestBalance),
            ),
            label: 'Remaining Interest Balance',
          },
          {
            value: getCurrencyFormat(
              Number(formattedTotalOutstandingPrincipalBalance),
            ),
            label: 'Remaining Principal Balance',
          },
          {
            value: getCurrencyFormat(netCashRecieved),
            label: 'Net Cash Recieved',
          },
        ]
      }

      case 'Prior Loan Information': {
        const previousLoan =
          application?.business?.loanHistory?.previousLoans[0]
        const totalCostOfCredit = previousLoan?.totalCostOfCredit.value
        const principalAmount = Number(previousLoan?.principalAmount.value)
        const previousLoanFee = roundOffNumberValue(
          Number(totalCostOfCredit) - Number(principalAmount),
        )
        const termInDays = getNumberFromString(
          previousLoan?.formattedTerm?.formattedTimeDuration,
        )
        const termInWeeks = convertTermToWeeks(termInDays)
        return [
          {
            value: getCurrencyFormat(principalAmount),
            label: 'Previous Loan Amount',
          },
          {
            value: getCurrencyFormat(previousLoanFee),
            label: 'Previous Loan Fee',
          },
          {
          value: `${previousLoan?.totalLoanTermInterestRate}%`,
            label: 'Previous Loan Interest Percentage',
          },
          {
            value: previousLoan?.grade,
            label: 'Previous Loan Grade',
          },
          {
            value: termInWeeks,
            label: 'Previous Loan Term in weeks',
          },
        ]
      }

      default:
        return []
    }
  }

  const OpportunityDetailInfo = ({ label, value }) => {
    return (
      <>
        <div className="loan-info-container">
          <span style={{ flex: '1', textAlign: 'left' }}>{label}</span>{' '}
          <span style={{ flex: '1', textAlign: 'center' }}>{value}</span>
        </div>
      </>
    )
  }

  return (
    <div>
      <Card size="xs">
        <Card.Header>
          <BodyText strong>{getCardHeader(contentCardType)}</BodyText>
        </Card.Header>
        <Card.Content>
          {getCardContent(contentCardType).map((item, index) => (
            <>
              <OpportunityDetailInfo
                key={index}
                label={item.label}
                value={item.value}
              />
              <Divider />
            </>
          ))}
        </Card.Content>
      </Card>
    </div>
  )
}

export default InformationCard
